import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Database,
  Loader2,
  RefreshCw,
  Search,
  Send,
  Settings as SettingsIcon,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { hostOpenapiAPI } from '../utils/api';
import type {
  OpenapiHostUpgradeRequestResponse,
  OpenapiHostReviewRequest,
  OpenapiCompanyListItem,
  OpenapiCompanyDataSource,
  OpenapiSettingsUpdate,
  OpenapiSettingsTestResponse,
} from '../types/openapi';

type Tab = 'usage' | 'requests' | 'cache' | 'settings';

const TABS: { key: Tab; label: string; icon: typeof TrendingUp }[] = [
  { key: 'usage', label: 'Usage', icon: TrendingUp },
  { key: 'requests', label: 'Upgrade requests', icon: Send },
  { key: 'cache', label: 'Aziende cached', icon: Database },
  { key: 'settings', label: 'Settings', icon: SettingsIcon },
];

function formatEur(v: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(v);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export default function OpenapiHostAdmin() {
  const [activeTab, setActiveTab] = useState<Tab>('usage');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Anagrafica Imprese — Host Admin</h1>
        <p className="text-sm text-gray-500 mt-1">
          Monitoraggio consumo cross-tenant, gestione richieste di upgrade e configurazione.
        </p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-3 px-1 inline-flex items-center gap-2 border-b-2 font-medium text-sm transition-colors ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'usage' && <UsageTab />}
      {activeTab === 'requests' && <UpgradeRequestsTab />}
      {activeTab === 'cache' && <CacheTab />}
      {activeTab === 'settings' && <SettingsTab />}
    </div>
  );
}

// =================== Usage tab ===================

function UsageTab() {
  const [from, setFrom] = useState<string>(isoDaysAgo(30));
  const [to, setTo] = useState<string>(todayIso());

  const usageQuery = useQuery({
    queryKey: ['host-openapi-usage', from, to],
    queryFn: async () => {
      const res = await hostOpenapiAPI.getUsage({ from, to });
      return res.data;
    },
  });

  const chartData = useMemo(() => {
    const top = usageQuery.data?.top_10_consumers ?? [];
    return top.map((row) => ({
      name: row.tenant_nome,
      base: row.count_base,
      advanced: row.count_advanced,
      visura: row.count_visura,
    }));
  }, [usageQuery.data]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-4 bg-white rounded-lg shadow p-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Da
          </label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            max={to}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            A
          </label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            min={from}
            max={todayIso()}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => usageQuery.refetch()}
          className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          Aggiorna
        </button>
      </div>

      {usageQuery.isLoading ? (
        <div className="bg-white rounded-lg shadow p-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : usageQuery.isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-sm text-red-800">
          <AlertCircle className="h-4 w-4" />
          Errore nel caricamento usage.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SummaryCard
              label="Costo totale periodo"
              value={formatEur(usageQuery.data?.grand_total_cost_eur ?? 0)}
            />
            <SummaryCard
              label="Tenant attivi"
              value={String(usageQuery.data?.by_tenant.length ?? 0)}
            />
            <SummaryCard
              label="Periodo"
              value={`${from} → ${to}`}
            />
          </div>

          {chartData.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Top 10 consumatori
              </h3>
              <div style={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      angle={-20}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="base" stackId="a" fill="#9CA3AF" />
                    <Bar dataKey="advanced" stackId="a" fill="#3B82F6" />
                    <Bar dataKey="visura" stackId="a" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-700">Tutti i tenant</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Tenant</th>
                    <th className="px-4 py-2 text-right">Base</th>
                    <th className="px-4 py-2 text-right">Advanced</th>
                    <th className="px-4 py-2 text-right">Visura</th>
                    <th className="px-4 py-2 text-right">Costo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(usageQuery.data?.by_tenant ?? []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                        Nessun consumo nel periodo selezionato.
                      </td>
                    </tr>
                  )}
                  {(usageQuery.data?.by_tenant ?? []).map((row) => (
                    <tr key={row.tenant_id}>
                      <td className="px-4 py-2 text-gray-900">{row.tenant_nome}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{row.count_base}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{row.count_advanced}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{row.count_visura}</td>
                      <td className="px-4 py-2 text-right text-gray-900 font-medium">
                        {formatEur(row.total_cost_eur)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="text-lg font-semibold text-gray-900 mt-1">{value}</div>
    </div>
  );
}

// =================== Upgrade requests tab ===================

function UpgradeRequestsTab() {
  const queryClient = useQueryClient();
  const [archiveExpanded, setArchiveExpanded] = useState(false);
  const [reviewing, setReviewing] = useState<OpenapiHostUpgradeRequestResponse | null>(null);

  const pendingQuery = useQuery({
    queryKey: ['host-openapi-upgrade', 'pending'],
    queryFn: async () => {
      const res = await hostOpenapiAPI.listUpgradeRequests({
        stato: 'pending',
        page: 1,
        size: 50,
      });
      return res.data;
    },
  });

  const archiveQuery = useQuery({
    queryKey: ['host-openapi-upgrade', 'archive'],
    queryFn: async () => {
      const res = await hostOpenapiAPI.listUpgradeRequests({ page: 1, size: 50 });
      return res.data;
    },
    enabled: archiveExpanded,
  });

  const archiveItems = useMemo(
    () =>
      (archiveQuery.data?.items ?? []).filter((it) => it.stato !== 'pending'),
    [archiveQuery.data]
  );

  const handleReviewed = () => {
    queryClient.invalidateQueries({ queryKey: ['host-openapi-upgrade'] });
    setReviewing(null);
  };

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b bg-amber-50 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-amber-900">
            Richieste in attesa
            {pendingQuery.data && (
              <span className="ml-2 text-xs font-normal text-amber-700">
                ({pendingQuery.data.total})
              </span>
            )}
          </h3>
          <button
            type="button"
            onClick={() => pendingQuery.refetch()}
            className="text-xs text-amber-800 hover:underline inline-flex items-center gap-1"
          >
            <RefreshCw className="h-3 w-3" />
            Aggiorna
          </button>
        </div>
        <UpgradeRequestList
          items={pendingQuery.data?.items ?? []}
          isLoading={pendingQuery.isLoading}
          onReview={setReviewing}
          showActions
        />
      </section>

      <section className="bg-white rounded-lg shadow overflow-hidden">
        <button
          type="button"
          onClick={() => setArchiveExpanded((v) => !v)}
          className="w-full px-4 py-3 border-b bg-gray-50 flex items-center justify-between hover:bg-gray-100"
        >
          <h3 className="text-sm font-semibold text-gray-700">
            Approvate / rifiutate
          </h3>
          {archiveExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
        </button>
        {archiveExpanded && (
          <UpgradeRequestList
            items={archiveItems}
            isLoading={archiveQuery.isLoading}
            onReview={setReviewing}
            showActions={false}
          />
        )}
      </section>

      {reviewing && (
        <ReviewModal
          request={reviewing}
          onClose={() => setReviewing(null)}
          onReviewed={handleReviewed}
        />
      )}
    </div>
  );
}

function UpgradeRequestList({
  items,
  isLoading,
  onReview,
  showActions,
}: {
  items: OpenapiHostUpgradeRequestResponse[];
  isLoading: boolean;
  onReview: (req: OpenapiHostUpgradeRequestResponse) => void;
  showActions: boolean;
}) {
  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-gray-500">
        Nessuna richiesta.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-gray-100">
      {items.map((req) => (
        <li key={req.id} className="p-4 flex flex-col sm:flex-row sm:items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-sm text-gray-900">
                {req.tenant.nome}
              </span>
              <StatoBadge stato={req.stato} />
              <span className="text-xs text-gray-500">
                {new Date(req.created_at).toLocaleString('it-IT')}
              </span>
            </div>
            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
              {req.motivazione}
            </p>
            <div className="text-xs text-gray-500 mt-1 space-x-3">
              {req.requested_quota_advanced_daily != null && (
                <span>Advanced/giorno richiesto: {req.requested_quota_advanced_daily}</span>
              )}
              {req.requested_quota_visura_monthly != null && (
                <span>Visure/mese richiesto: {req.requested_quota_visura_monthly}</span>
              )}
            </div>
            {req.host_response_note && (
              <p className="text-xs text-gray-500 mt-1 italic">
                Nota host: {req.host_response_note}
              </p>
            )}
          </div>
          {showActions && req.stato === 'pending' && (
            <button
              type="button"
              onClick={() => onReview(req)}
              className="self-start inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs"
            >
              Esamina
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function StatoBadge({ stato }: { stato: string }) {
  const map: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-700',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${
        map[stato] ?? 'bg-gray-100 text-gray-700'
      }`}
    >
      {stato}
    </span>
  );
}

function ReviewModal({
  request,
  onClose,
  onReviewed,
}: {
  request: OpenapiHostUpgradeRequestResponse;
  onClose: () => void;
  onReviewed: () => void;
}) {
  const [stato, setStato] = useState<'approved' | 'rejected'>('approved');
  const [note, setNote] = useState('');
  const [overrideAdvanced, setOverrideAdvanced] = useState<string>(
    request.requested_quota_advanced_daily?.toString() ?? ''
  );
  const [overrideVisura, setOverrideVisura] = useState<string>(
    request.requested_quota_visura_monthly?.toString() ?? ''
  );

  const reviewMutation = useMutation({
    mutationFn: () => {
      const advParsed = parseInt(overrideAdvanced, 10);
      const visParsed = parseInt(overrideVisura, 10);
      const payload: OpenapiHostReviewRequest = {
        stato,
        host_response_note: note.trim() || undefined,
        override_quota_advanced_daily:
          stato === 'approved' && Number.isFinite(advParsed) && advParsed > 0
            ? advParsed
            : null,
        override_quota_visura_monthly:
          stato === 'approved' && Number.isFinite(visParsed) && visParsed > 0
            ? visParsed
            : null,
      };
      return hostOpenapiAPI.reviewUpgradeRequest(request.id, payload);
    },
    onSuccess: () => {
      toast.success(stato === 'approved' ? 'Richiesta approvata' : 'Richiesta rifiutata');
      onReviewed();
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      if (status === 409) {
        toast.error('Richiesta già processata, ricarica la lista');
        return;
      }
      toast.error(error?.response?.data?.detail || 'Errore durante la review');
    },
  });

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-lg font-semibold">
            Review richiesta — {request.tenant.nome}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm space-y-1">
            <p className="text-gray-700 whitespace-pre-wrap">{request.motivazione}</p>
            <div className="text-xs text-gray-500 space-x-3">
              {request.requested_quota_advanced_daily != null && (
                <span>Advanced/giorno richiesto: {request.requested_quota_advanced_daily}</span>
              )}
              {request.requested_quota_visura_monthly != null && (
                <span>Visure/mese richiesto: {request.requested_quota_visura_monthly}</span>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>Decisione</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStato('approved')}
                className={`flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm border ${
                  stato === 'approved'
                    ? 'bg-green-600 border-green-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Check className="h-4 w-4" />
                Approva
              </button>
              <button
                type="button"
                onClick={() => setStato('rejected')}
                className={`flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm border ${
                  stato === 'rejected'
                    ? 'bg-red-600 border-red-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <X className="h-4 w-4" />
                Rifiuta
              </button>
            </div>
          </div>

          {stato === 'approved' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>
                  Override quota advanced/giorno
                </label>
                <input
                  type="number"
                  min={1}
                  value={overrideAdvanced}
                  onChange={(e) => setOverrideAdvanced(e.target.value)}
                  placeholder="Lascia vuoto per default piano"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Override quota visure/mese
                </label>
                <input
                  type="number"
                  min={1}
                  value={overrideVisura}
                  onChange={(e) => setOverrideVisura(e.target.value)}
                  placeholder="Lascia vuoto per default piano"
                  className={inputClass}
                />
              </div>
            </div>
          )}

          <div>
            <label className={labelClass}>Nota (opzionale)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Comunicazione visibile al tenant"
              className={inputClass}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={reviewMutation.isPending}
            onClick={() => reviewMutation.mutate()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            {reviewMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Conferma decisione
          </button>
        </div>
      </div>
    </div>
  );
}

// =================== Cache tab ===================

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function formatPct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT');
}

const DATA_SOURCE_LABEL: Record<OpenapiCompanyDataSource, string> = {
  bulk_import: 'Bulk import',
  tenant_validate: 'Tenant validate',
  tenant_search: 'Tenant search',
  tenant_visura: 'Tenant visura',
  manual_refresh: 'Manual refresh',
};

const DATA_SOURCE_BADGE: Record<OpenapiCompanyDataSource, string> = {
  bulk_import: 'bg-purple-100 text-purple-800',
  tenant_validate: 'bg-blue-100 text-blue-800',
  tenant_search: 'bg-cyan-100 text-cyan-800',
  tenant_visura: 'bg-emerald-100 text-emerald-800',
  manual_refresh: 'bg-amber-100 text-amber-800',
};

function CacheTab() {
  const queryClient = useQueryClient();

  // Filtri
  const [qInput, setQInput] = useState('');
  const q = useDebounced(qInput, 300);
  const [provincia, setProvincia] = useState('');
  const [attivo, setAttivo] = useState<'' | 'true' | 'false'>('');
  const [hasVisura, setHasVisura] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [detailId, setDetailId] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [q, provincia, attivo, hasVisura]);

  const statsQuery = useQuery({
    queryKey: ['host-openapi-cache', 'stats', 30],
    queryFn: async () => {
      const res = await hostOpenapiAPI.getCacheStats({ window_days: 30 });
      return res.data;
    },
  });

  const listQuery = useQuery({
    queryKey: [
      'host-openapi-cache',
      'list',
      { q, provincia, attivo, hasVisura, page },
    ],
    queryFn: async () => {
      const res = await hostOpenapiAPI.listCachedCompanies({
        q: q.trim() || undefined,
        provincia: provincia.trim().toUpperCase() || undefined,
        attivo: attivo === '' ? undefined : attivo === 'true',
        has_visura: hasVisura === '' ? undefined : hasVisura === 'true',
        page,
        page_size: pageSize,
      });
      return res.data;
    },
    placeholderData: keepPreviousData,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => hostOpenapiAPI.deleteCachedCompany(id),
    onSuccess: () => {
      toast.success('Azienda rimossa dalla cache');
      queryClient.invalidateQueries({ queryKey: ['host-openapi-cache'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Errore durante la rimozione');
    },
  });

  const handleDelete = (item: OpenapiCompanyListItem) => {
    if (
      !window.confirm(
        `Rimuovere "${item.denominazione || item.partita_iva}" dalla cache?\n\nIl record sarà soft-deleted e non sarà più disponibile ai tenant.`
      )
    ) {
      return;
    }
    deleteMutation.mutate(item.id);
  };

  const stats = statsQuery.data;
  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          label="Totale aziende"
          value={statsQuery.isLoading ? '…' : String(stats?.total_companies ?? 0)}
        />
        <SummaryCard
          label="Cache hit rate (30gg)"
          value={statsQuery.isLoading ? '…' : formatPct(stats?.cache_hit_rate ?? 0)}
        />
        <SummaryCard
          label="Risparmio stimato (30gg)"
          value={
            statsQuery.isLoading
              ? '…'
              : formatEur(Number(stats?.estimated_savings_eur ?? 0))
          }
        />
        <SummaryCard
          label="Ultimo bulk import"
          value={statsQuery.isLoading ? '…' : formatDateTime(stats?.last_import_at)}
        />
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-lg shadow p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="lg:col-span-2">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Cerca
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Denominazione, P.IVA o codice fiscale"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Provincia
          </label>
          <input
            type="text"
            maxLength={2}
            value={provincia}
            onChange={(e) => setProvincia(e.target.value.toUpperCase())}
            placeholder="es. MI"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Attivo
          </label>
          <select
            value={attivo}
            onChange={(e) => setAttivo(e.target.value as '' | 'true' | 'false')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="">Tutti</option>
            <option value="true">Sì</option>
            <option value="false">No</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Visura disponibile
          </label>
          <select
            value={hasVisura}
            onChange={(e) => setHasVisura(e.target.value as '' | 'true' | 'false')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="">Tutte</option>
            <option value="true">Sì</option>
            <option value="false">No</option>
          </select>
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-700">
              Aziende in cache
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({total})
              </span>
            </h3>
          </div>
          <button
            type="button"
            onClick={() => listQuery.refetch()}
            className="text-xs text-gray-700 hover:underline inline-flex items-center gap-1"
          >
            <RefreshCw className="h-3 w-3" />
            Aggiorna
          </button>
        </div>

        {listQuery.isLoading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : listQuery.isError ? (
          <div className="p-4 bg-red-50 border-t border-red-200 text-sm text-red-800 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Errore nel caricamento aziende cache.
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            Nessuna azienda in cache con i filtri selezionati.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left">P.IVA</th>
                  <th className="px-4 py-2 text-left">Denominazione</th>
                  <th className="px-4 py-2 text-left">Provincia</th>
                  <th className="px-4 py-2 text-left">Attivo</th>
                  <th className="px-4 py-2 text-left">Ultimo refresh</th>
                  <th className="px-4 py-2 text-left">Source</th>
                  <th className="px-4 py-2 text-left">Visura</th>
                  <th className="px-4 py-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">
                      {item.partita_iva}
                    </td>
                    <td className="px-4 py-2 text-gray-900">
                      <button
                        type="button"
                        onClick={() => setDetailId(item.id)}
                        className="text-blue-600 hover:underline text-left"
                      >
                        {item.denominazione || '—'}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {item.provincia || '—'}
                    </td>
                    <td className="px-4 py-2">
                      {item.attivo == null ? (
                        <span className="text-gray-400">—</span>
                      ) : item.attivo ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-green-100 text-green-800">
                          Sì
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-700">
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-700 text-xs">
                      {formatDateTime(item.last_refreshed_at)}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${DATA_SOURCE_BADGE[item.data_source]}`}
                      >
                        {DATA_SOURCE_LABEL[item.data_source]}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {item.has_visura ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-100 text-emerald-800">
                          Sì
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setDetailId(item.id)}
                          className="px-2 py-1 text-xs text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded"
                        >
                          Dettagli
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          disabled={deleteMutation.isPending}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50"
                          title="Soft delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Pagina {page} di {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || listQuery.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-white disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                type="button"
                disabled={page >= totalPages || listQuery.isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-white disabled:opacity-50"
              >
                Successiva
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Top consumers */}
      {stats && stats.top_consumers.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">
              Top consumatori cache (30gg)
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left">Tenant</th>
                  <th className="px-4 py-2 text-right">Lookups</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stats.top_consumers.map((tc) => (
                  <tr key={tc.tenant_id}>
                    <td className="px-4 py-2 text-gray-900">{tc.tenant_nome}</td>
                    <td className="px-4 py-2 text-right text-gray-700">{tc.lookups}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detailId && (
        <CachedCompanyDetailDialog
          companyId={detailId}
          onClose={() => setDetailId(null)}
        />
      )}
    </div>
  );
}

function CachedCompanyDetailDialog({
  companyId,
  onClose,
}: {
  companyId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [showRawAdvanced, setShowRawAdvanced] = useState(false);
  const [showRawVisura, setShowRawVisura] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['host-openapi-cache', 'detail', companyId],
    queryFn: async () => {
      const res = await hostOpenapiAPI.getCachedCompany(companyId);
      return res.data;
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => hostOpenapiAPI.refreshCachedCompany(companyId),
    onSuccess: () => {
      toast.success('Refresh completato');
      queryClient.invalidateQueries({ queryKey: ['host-openapi-cache'] });
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      if (status === 503) {
        toast.error('Servizio non configurato (env vars mancanti)');
        return;
      }
      if (status === 502) {
        toast.error('Errore upstream OpenAPI.com, riprova');
        return;
      }
      toast.error(error?.response?.data?.detail || 'Errore durante il refresh');
    },
  });

  const handleRefresh = () => {
    if (
      !window.confirm(
        'Refresh manuale: chiama OpenAPI.com per ri-scaricare i dati. Il costo è a tuo carico (host). Procedere?'
      )
    ) {
      return;
    }
    refreshMutation.mutate();
  };

  const c = detailQuery.data;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">
            {detailQuery.isLoading ? 'Caricamento…' : c?.denominazione || '—'}
            {c && (
              <span className="ml-2 text-xs font-mono text-gray-500">
                {c.partita_iva}
              </span>
            )}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {detailQuery.isLoading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : detailQuery.isError || !c ? (
          <div className="p-6 text-sm text-red-700 bg-red-50">
            Errore nel caricamento del record.
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Anagrafica base */}
            <Section title="Anagrafica">
              <KV label="Codice fiscale" value={c.codice_fiscale} mono />
              <KV
                label="Forma giuridica"
                value={
                  c.legal_form_desc
                    ? `${c.legal_form_desc}${c.legal_form_code ? ` (${c.legal_form_code})` : ''}`
                    : null
                }
              />
              <KV label="Attivo" value={c.attivo == null ? null : c.attivo ? 'Sì' : 'No'} />
              <KV label="PEC" value={c.pec} />
              <KV label="REA" value={c.rea_code} mono />
              <KV label="CCIAA" value={c.cciaa} />
              <KV label="Data inizio" value={formatDateTime(c.start_date)} />
              <KV label="Data fine" value={formatDateTime(c.end_date)} />
            </Section>

            {/* Indirizzo */}
            <Section title="Indirizzo legale">
              <KV
                label="Via"
                value={[
                  c.indirizzo.toponym,
                  c.indirizzo.street,
                  c.indirizzo.civico,
                ]
                  .filter(Boolean)
                  .join(' ') || null}
              />
              <KV label="CAP" value={c.indirizzo.cap} />
              <KV label="Comune" value={c.indirizzo.comune} />
              <KV label="Provincia" value={c.indirizzo.provincia} />
              <KV label="Regione" value={c.indirizzo.regione} />
              <KV
                label="GPS"
                value={
                  c.indirizzo.gps_lat != null && c.indirizzo.gps_lon != null
                    ? `${c.indirizzo.gps_lat}, ${c.indirizzo.gps_lon}`
                    : null
                }
                mono
              />
            </Section>

            {/* ATECO */}
            <Section title="ATECO">
              <KV label="Codice" value={c.ateco.codice} mono />
              <KV label="Descrizione" value={c.ateco.descrizione} />
              <KV label="Versione" value={c.ateco.versione} />
            </Section>

            {/* Bilancio */}
            <Section title="Bilancio (ultimo)">
              <KV label="Anno" value={c.bilancio.last_balance_year?.toString()} />
              <KV label="Dipendenti" value={c.bilancio.dipendenti?.toString()} />
              <KV
                label="Fatturato"
                value={c.bilancio.fatturato != null ? formatEur(Number(c.bilancio.fatturato)) : null}
              />
              <KV
                label="Capitale sociale"
                value={
                  c.bilancio.capitale_sociale != null
                    ? formatEur(Number(c.bilancio.capitale_sociale))
                    : null
                }
              />
            </Section>

            {/* Soci */}
            {c.soci && c.soci.length > 0 && (
              <Section title={`Soci (${c.soci.length})`}>
                <div className="col-span-2">
                  <ul className="text-sm space-y-1">
                    {c.soci.map((s, i) => (
                      <li key={i} className="text-gray-700">
                        <pre className="font-mono text-[11px] bg-gray-50 p-2 rounded whitespace-pre-wrap">
                          {JSON.stringify(s, null, 2)}
                        </pre>
                      </li>
                    ))}
                  </ul>
                </div>
              </Section>
            )}

            {/* Amministratori */}
            {c.amministratori && c.amministratori.length > 0 && (
              <Section title={`Amministratori (${c.amministratori.length})`}>
                <div className="col-span-2">
                  <ul className="text-sm space-y-1">
                    {c.amministratori.map((a, i) => (
                      <li key={i} className="text-gray-700">
                        <pre className="font-mono text-[11px] bg-gray-50 p-2 rounded whitespace-pre-wrap">
                          {JSON.stringify(a, null, 2)}
                        </pre>
                      </li>
                    ))}
                  </ul>
                </div>
              </Section>
            )}

            {/* Metadata */}
            <Section title="Cache metadata">
              <KV
                label="Source"
                value={DATA_SOURCE_LABEL[c.data_source]}
              />
              <KV label="Importato il" value={formatDateTime(c.imported_at)} />
              <KV label="Ultimo refresh" value={formatDateTime(c.last_refreshed_at)} />
              <KV label="Visura PDF S3" value={c.visura_pdf_s3_key} mono />
            </Section>

            {/* Raw responses collapsible */}
            {c.raw_response_advanced && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowRawAdvanced((v) => !v)}
                  className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                >
                  {showRawAdvanced ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                  raw_response_advanced
                </button>
                {showRawAdvanced && (
                  <pre className="mt-2 font-mono text-[11px] bg-gray-50 p-3 rounded max-h-80 overflow-auto whitespace-pre-wrap">
                    {JSON.stringify(c.raw_response_advanced, null, 2)}
                  </pre>
                )}
              </div>
            )}
            {c.raw_response_visura && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowRawVisura((v) => !v)}
                  className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                >
                  {showRawVisura ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                  raw_response_visura
                </button>
                {showRawVisura && (
                  <pre className="mt-2 font-mono text-[11px] bg-gray-50 p-3 rounded max-h-80 overflow-auto whitespace-pre-wrap">
                    {JSON.stringify(c.raw_response_visura, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between items-center gap-3 p-6 border-t sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshMutation.isPending || detailQuery.isLoading || !c}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm"
            title="Re-fetch da OpenAPI.com — costo a carico host"
          >
            {refreshMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh (host-paid)
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        {title}
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {children}
      </div>
    </div>
  );
}

function KV({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500">{label}</span>
      <span
        className={`text-gray-900 ${mono ? 'font-mono text-xs' : ''} ${!value ? 'text-gray-400 italic' : ''}`}
      >
        {value || '—'}
      </span>
    </div>
  );
}

// =================== Settings tab ===================

const HTTPS_RE = /^https:\/\/.+/i;

function SettingsTab() {
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ['host-openapi-settings'],
    queryFn: async () => {
      const res = await hostOpenapiAPI.getSettings();
      return res.data;
    },
  });

  // Form state — controllato. Inizializzato da settingsQuery quando arriva.
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [visureBaseUrl, setVisureBaseUrl] = useState('');
  const [useSandbox, setUseSandbox] = useState(true);
  const [showSecret, setShowSecret] = useState(false);

  const [testResult, setTestResult] = useState<OpenapiSettingsTestResponse | null>(null);

  // Sync form quando i settings vengono caricati / aggiornati
  useEffect(() => {
    if (!settingsQuery.data) return;
    const s = settingsQuery.data;
    setClientId(s.client_id ?? '');
    setBaseUrl(s.base_url);
    setVisureBaseUrl(s.visure_base_url);
    setUseSandbox(s.use_sandbox);
    // client_secret non viene mai dal backend → reset campo a vuoto
    setClientSecret('');
    setTestResult(null);
  }, [settingsQuery.data]);

  // Validazione URL HTTPS
  const baseUrlValid = HTTPS_RE.test(baseUrl);
  const visureBaseUrlValid = HTTPS_RE.test(visureBaseUrl);
  const urlsValid = baseUrlValid && visureBaseUrlValid;

  const updateMutation = useMutation({
    mutationFn: (payload: OpenapiSettingsUpdate) =>
      hostOpenapiAPI.updateSettings(payload),
    onSuccess: () => {
      toast.success('Impostazioni salvate');
      queryClient.invalidateQueries({ queryKey: ['host-openapi-settings'] });
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Errore nel salvataggio'
      );
    },
  });

  const testMutation = useMutation({
    mutationFn: () =>
      hostOpenapiAPI.testSettings({
        client_id: clientId.trim() || undefined,
        client_secret: clientSecret || undefined,
        base_url: baseUrl.trim() || undefined,
      }),
    onSuccess: (res) => {
      setTestResult(res.data);
      if (res.data.ok) {
        toast.success('Connessione OK');
      } else {
        toast.error(res.data.error || 'Test fallito');
      }
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Errore nel test connessione'
      );
    },
  });

  const handleSave = () => {
    if (!urlsValid) {
      toast.error('Gli URL devono iniziare con https://');
      return;
    }
    const secretChanged = clientSecret !== '';
    if (
      secretChanged &&
      !window.confirm(
        'Stai aggiornando il client_secret. Il nuovo valore verrà cifrato e salvato. Procedere?'
      )
    ) {
      return;
    }
    const payload: OpenapiSettingsUpdate = {
      client_id: clientId.trim() || null,
      base_url: baseUrl.trim(),
      visure_base_url: visureBaseUrl.trim(),
      use_sandbox: useSandbox,
    };
    // Semantica secret: solo se l'utente ha digitato qualcosa includiamo il campo.
    // Per "clear" il campo va impostato esplicitamente a null da un'azione UI dedicata
    // (vedi pulsante "Rimuovi secret").
    if (clientSecret !== '') {
      payload.client_secret = clientSecret;
    }
    updateMutation.mutate(payload);
  };

  const handleClearSecret = () => {
    if (
      !window.confirm(
        'Rimuovere il client_secret salvato? Il backend tornerà in stato "non configurato" finché non ne imposti uno nuovo.'
      )
    ) {
      return;
    }
    updateMutation.mutate({ client_secret: null });
  };

  const isLoading = settingsQuery.isLoading;
  const settings = settingsQuery.data;

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const errorClass = 'mt-1 text-xs text-red-600';
  const helpClass = 'mt-1 text-xs text-gray-500';

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
        <p className="font-medium mb-1">Credenziali Anagrafica Imprese</p>
        <p className="text-amber-800">
          Le credenziali sono cifrate a riposo (Fernet). Solo l'host può visualizzarle e
          modificarle. Il valore plaintext non viene mai esposto dal backend.
        </p>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : settingsQuery.isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-sm text-red-800">
          <AlertCircle className="h-4 w-4" />
          Errore nel caricamento delle impostazioni.
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow p-6 space-y-5">
            <div>
              <label className={labelClass}>Client ID</label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="OAuth 2.0 client ID"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Client Secret</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder={
                      settings?.has_client_secret ? '••••••••' : '(non configurato)'
                    }
                    autoComplete="new-password"
                    className={inputClass}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowSecret((v) => !v)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-xs hover:bg-gray-50"
                >
                  {showSecret ? 'Nascondi' : 'Mostra'}
                </button>
                {settings?.has_client_secret && (
                  <button
                    type="button"
                    onClick={handleClearSecret}
                    disabled={updateMutation.isPending}
                    className="px-3 py-2 border border-red-300 text-red-700 rounded-lg text-xs hover:bg-red-50 disabled:opacity-50"
                    title="Rimuove il secret salvato"
                  >
                    Rimuovi
                  </button>
                )}
              </div>
              <p className={helpClass}>
                Lascia vuoto per non modificare il valore corrente. Inserisci un valore
                per aggiornarlo (verrà cifrato a riposo).
              </p>
            </div>

            <div>
              <label className={labelClass}>Base URL — Imprese</label>
              <input
                type="url"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://test.imprese.openapi.it"
                className={inputClass}
              />
              {!baseUrlValid && baseUrl !== '' && (
                <p className={errorClass}>L'URL deve iniziare con https://</p>
              )}
            </div>

            <div>
              <label className={labelClass}>Base URL — Visure</label>
              <input
                type="url"
                value={visureBaseUrl}
                onChange={(e) => setVisureBaseUrl(e.target.value)}
                placeholder="https://test.visurecamerali.openapi.it"
                className={inputClass}
              />
              {!visureBaseUrlValid && visureBaseUrl !== '' && (
                <p className={errorClass}>L'URL deve iniziare con https://</p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useSandbox}
                  onChange={(e) => setUseSandbox(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Sandbox</span>
              </label>
              <span className="text-xs text-gray-500">
                {useSandbox
                  ? 'Modalità test (nessun costo).'
                  : 'PRODUZIONE: ogni call ha un costo reale.'}
              </span>
            </div>

            {testResult && (
              <div
                className={`rounded-lg p-3 text-sm flex items-start gap-2 border ${
                  testResult.ok
                    ? 'bg-green-50 border-green-200 text-green-900'
                    : 'bg-red-50 border-red-200 text-red-900'
                }`}
              >
                {testResult.ok ? (
                  <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  {testResult.ok ? (
                    <p>
                      Connessione OK — token expires in{' '}
                      <span className="font-mono">{testResult.expires_in ?? '—'}</span> sec.
                    </p>
                  ) : (
                    <p>Errore: {testResult.error || 'unknown'}</p>
                  )}
                  <p className="text-xs opacity-70 mt-0.5">
                    Tested at {new Date(testResult.tested_at).toLocaleString('it-IT')}
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
              <button
                type="button"
                onClick={() => testMutation.mutate()}
                disabled={
                  testMutation.isPending || !urlsValid || !clientId.trim()
                }
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
                title="Testa le credenziali del form (non salvate)"
              >
                {testMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Test connection
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={updateMutation.isPending || !urlsValid}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Salva
              </button>
              <span className="ml-auto text-xs text-gray-500">
                Stato: {settings?.has_client_secret ? (
                  <span className="text-green-700 font-medium">configurato</span>
                ) : (
                  <span className="text-amber-700 font-medium">non configurato</span>
                )}
                {settings?.updated_at && (
                  <>
                    {' · ultimo aggiornamento '}
                    {new Date(settings.updated_at).toLocaleString('it-IT')}
                  </>
                )}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
